"use client"

import { useSession, signOut } from "next-auth/react"
import { Button } from "@/components/ui/button"
import { LogOut, CheckCircle, Clock, FileCheck, ArrowRight } from "lucide-react"
import { useRouter } from "next/navigation"
import { useState, useEffect } from "react"
import { FluxLogo } from "@/components/ui/flux-logo"

interface WaitingApprovalScreenProps {
  onSignOut?: () => void
}

export default function WaitingApprovalScreen({ onSignOut }: WaitingApprovalScreenProps) {
  const { data: session } = useSession()
  const router = useRouter()
  const [isSigningOut, setIsSigningOut] = useState(false)

  // Auto-refresh every 60 seconds to check approval status
  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload()
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      if (onSignOut) {
        onSignOut()
      } else {
        await signOut({ callbackUrl: "/" })
      }
    } catch (error) {
      console.error("Error signing out:", error)
    } finally {
      setIsSigningOut(false)
    }
  }

  const isMerchant = session?.user?.role === "MERCHANT"

  const steps = [
    {
      label: "Submitted",
      icon: FileCheck,
      status: "complete" as const,
    },
    {
      label: "Under Review",
      icon: Clock,
      status: "active" as const,
    },
    {
      label: "Approved",
      icon: CheckCircle,
      status: "pending" as const,
    },
  ]

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-to-b from-primary/[0.06] via-primary/[0.02] to-transparent rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-2xl">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <FluxLogo size="lg" />
          </div>

          {/* Headline */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-3">
              You&apos;re Almost In
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Your application has been submitted and is currently being reviewed by our team.
              We&apos;ll notify you as soon as it&apos;s approved.
            </p>
          </div>

          {/* 3-Step Progress */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isComplete = step.status === "complete"
              const isActive = step.status === "active"
              const isPending = step.status === "pending"

              return (
                <div key={step.label} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 mb-2 transition-all ${
                        isComplete
                          ? "bg-primary/20 border-primary"
                          : isActive
                          ? "bg-primary/10 border-primary ring-4 ring-primary/20"
                          : "bg-muted border-border"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${
                          isComplete
                            ? "text-primary"
                            : isActive
                            ? "text-primary animate-pulse"
                            : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        isComplete || isActive ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-px flex-1 mx-2 mb-5 ${
                        index === 0 ? "bg-primary/50" : "bg-border"
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* Timeline note */}
          <div className="bg-muted border border-border rounded-xl p-4 mb-6 text-center">
            <p className="text-sm text-muted-foreground">
              <span className="text-primary font-medium">Typical approval:</span>{" "}
              1–2 business days
            </p>
          </div>

          {/* KYB CTA for merchants */}
          {isMerchant && (
            <div className="mb-4">
              <button
                onClick={() => router.push("/dashboard/kyb")}
                className="w-full flex items-center justify-between px-4 py-3 bg-primary/10 border border-primary/30 rounded-xl hover:bg-primary/15 transition-all group"
              >
                <div className="text-left">
                  <p className="text-sm font-medium text-primary">Complete KYB to speed up approval</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Business verification helps us approve you faster</p>
                </div>
                <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          )}

          {/* Support */}
          <div className="text-center mb-6">
            <p className="text-xs text-muted-foreground">
              Need help?{" "}
              <a
                href="mailto:support@fluxpay.online"
                className="text-primary hover:text-primary/80 transition-colors"
              >
                Contact support
              </a>
            </p>
          </div>

          {/* Sign Out */}
          <Button
            onClick={handleSignOut}
            variant="outline"
            className="w-full border-border text-muted-foreground hover:bg-muted hover:text-foreground"
            disabled={isSigningOut}
          >
            <LogOut className="w-4 h-4 mr-2" />
            {isSigningOut ? "Signing out..." : "Sign Out"}
          </Button>
        </div>
      </div>
    </div>
  )
}
